<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">
                        Normativas de Trabalho
                    </v-toolbar-title>
                    <v-spacer/>
                </v-toolbar>

                <v-card elevation="2" class="ma-1 mb-6">
                    <v-card-text class="pa-0">
                        <div class="doc-capa pa-6 text-center">
                            <v-icon size="42" color="accent">mdi-file-document-check-outline</v-icon>
                            <h1 class="doc-titulo mt-3">
                                Normativas de Trabalho Home Office
                            </h1>
                            <div class="doc-subtitulo mt-1">ZaperMenu</div>
                            <v-chip small outlined class="mt-4">
                                <v-icon left small>mdi-update</v-icon>
                                Última atualização: {{ ultimaAtualizacao }}
                            </v-chip>
                        </div>

                        <v-divider/>

                        <div v-if="$vuetify.breakpoint.smAndUp" class="pa-4 doc-indice">
                            <v-chip
                                v-for="s in secoes"
                                :key="`idx-${s.numero}`"
                                small
                                label
                                outlined
                                class="ma-1"
                                @click="irPara(s.numero)"
                            >
                                {{ s.numero }}. {{ s.titulo }}
                            </v-chip>
                        </div>

                        <v-divider v-if="$vuetify.breakpoint.smAndUp"/>

                        <div class="doc-corpo pa-6">
                            <section
                                v-for="s in secoes"
                                :key="s.numero"
                                :id="`secao-${s.numero}`"
                                class="doc-secao"
                            >
                                <div class="d-flex align-center mb-4">
                                    <v-avatar color="accent" size="34" class="mr-3">
                                        <span class="white--text font-weight-bold">{{ s.numero }}</span>
                                    </v-avatar>
                                    <h2 class="doc-secao-titulo">
                                        <v-icon small class="mr-1 mb-1">{{ s.icone }}</v-icon>
                                        {{ s.titulo }}
                                    </h2>
                                </div>

                                <template v-for="(bloco, i) in s.blocos">
                                    <p
                                        v-if="bloco.tipo === 'texto'"
                                        :key="`${s.numero}-${i}`"
                                        class="doc-texto"
                                    >
                                        {{ bloco.valor }}
                                    </p>

                                    <ul
                                        v-else-if="bloco.tipo === 'lista'"
                                        :key="`${s.numero}-${i}`"
                                        class="doc-lista"
                                    >
                                        <li v-for="(item, j) in bloco.valor" :key="j">
                                            {{ item }}
                                        </li>
                                    </ul>

                                    <v-alert
                                        v-else-if="bloco.tipo === 'destaque'"
                                        :key="`${s.numero}-${i}`"
                                        dense
                                        text
                                        type="warning"
                                        icon="mdi-alert"
                                        class="doc-destaque"
                                    >
                                        {{ bloco.valor }}
                                    </v-alert>
                                </template>

                                <v-divider class="mt-6 mb-8 doc-separador"/>
                            </section>

                            <div class="text-center doc-rodape">
                                Em caso de dúvidas, procure seu gestor direto.
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';

export default {
    name: 'Normativas',

    components: {
        BaseContainer,
    },

    data: () => ({
        ultimaAtualizacao: '28/08/2026',

        secoes: [
            {
                numero: 1,
                titulo: 'Objetivo',
                icone: 'mdi-target',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'Este documento tem como finalidade estabelecer diretrizes claras para a '
                            + 'realização das atividades em regime de home office pelos colaboradores do '
                            + 'ZaperMenu, sejam contratados via CLT ou prestadores de serviço (freelancers), '
                            + 'assegurando um ambiente de trabalho profissional, produtivo e em sintonia com '
                            + 'a cultura de alta performance da nossa empresa.',
                    },
                ],
            },
            {
                numero: 2,
                titulo: 'Postura e Responsabilidade',
                icone: 'mdi-account-star',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'O trabalho remoto permite maior conforto e flexibilidade, mas exige '
                            + 'responsabilidade e comprometimento com os resultados. Espera-se de todos '
                            + 'os colaboradores:',
                    },
                    {
                        tipo: 'lista',
                        valor: [
                            'Dedicação durante o horário de expediente acordado.',
                            'Cumprimento de prazos e metas com foco total nas entregas.',
                            'Postura profissional em todas as interações, mesmo em um ambiente informal.',
                            'Proatividade para buscar soluções e colaborar com o time.',
                        ],
                    },
                ],
            },
            {
                numero: 3,
                titulo: 'Equipamentos e Estrutura Física',
                icone: 'mdi-desktop-tower-monitor',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'Para garantir qualidade no desempenho, é obrigatório o uso de:',
                    },
                    {
                        tipo: 'lista',
                        valor: [
                            'Mouse e teclado externos.',
                            'Suporte para notebook, de modo que a tela fique na altura dos olhos.',
                            'Monitor adicional (quando fornecido).',
                            'Cadeira e mesa adequadas ao trabalho.',
                            'Iluminação apropriada — nem escura, nem excessivamente clara.',
                            'Ambiente silencioso e livre de distrações (evitar trabalhar da cama ou sofá, '
                                + 'por exemplo).',
                            'Conexão de internet estável, adequada para chamadas de vídeo, envio de arquivos '
                                + 'e uso contínuo das ferramentas da empresa.',
                        ],
                    },
                    {
                        tipo: 'texto',
                        valor: 'Equipamentos fornecidos pela empresa devem ser usados exclusivamente para '
                            + 'atividades do ZaperMenu. É responsabilidade do colaborador zelar por sua '
                            + 'integridade. Em caso de mau uso ou danos, poderão ser solicitados reparos '
                            + 'ou reposições.',
                    },
                    {
                        tipo: 'texto',
                        valor: 'Se houver perda ou furto de notebook, celular ou acessos, o colaborador '
                            + 'deve avisar imediatamente a empresa.',
                    },
                ],
            },
            {
                numero: 4,
                titulo: 'Segurança da Informação',
                icone: 'mdi-shield-lock',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'A segurança dos dados e da operação é prioridade. São diretrizes obrigatórias:',
                    },
                    {
                        tipo: 'lista',
                        valor: [
                            'Utilizar redes Wi-Fi seguras. Evitar redes públicas sem uso de VPN.',
                            'Nunca compartilhar logins e senhas com terceiros.',
                            'Bloquear o computador sempre que se ausentar.',
                            'Usar senhas fortes e, quando possível, autenticação em dois fatores.',
                            'As senhas são de responsabilidade de cada usuário, devendo ser mantida em '
                                + 'sigilo e protegida contra acessos indevidos.',
                            'Armazenar arquivos e informações apenas em ambientes autorizados pela empresa '
                                + '(ex: Google Drive, sistemas internos).',
                            'Conversas, informações estratégicas e dados de clientes não podem ser '
                                + 'compartilhados fora dos canais oficiais. Todo material produzido e trocado '
                                + 'pertence à empresa e deve ser tratado em sigilo.',
                            'As reuniões da empresa (Google Meet, Zoom, Discord ou outras) podem ser gravadas '
                                + 'pela liderança, mediante aviso prévio.',
                        ],
                    },
                    {
                        tipo: 'texto',
                        valor: 'É autorizado o uso do ChatGPT, via conta com acesso Plus compartilhada '
                            + 'pela empresa.',
                    },
                    {
                        tipo: 'destaque',
                        valor: 'É proibido compartilhar dados sensíveis, informações estratégicas, senha ou '
                            + 'tokens de acesso em ferramentas externas.',
                    },
                ],
            },
            {
                numero: 5,
                titulo: 'Comunicação e Disponibilidade',
                icone: 'mdi-message-text',
                blocos: [
                    {
                        tipo: 'lista',
                        valor: [
                            'Manter comunicação ágil durante todo o horário de trabalho, respondendo mensagens '
                                + 'e solicitações o mais breve possível. Evitar que clientes, colegas ou gestores '
                                + 'permaneçam aguardando sem qualquer retorno por mais de 15 minutos. Caso não '
                                + 'seja possível resolver a demanda de imediato, confirmar o recebimento e '
                                + 'fornecer um posicionamento.',
                            'Comunicar imediatamente aos colegas de trabalho do turno e aos gestores qualquer '
                                + 'situação adversa ocorrida durante o expediente que possa impactar o desempenho, '
                                + 'a disponibilidade ou o trabalho em equipe, como problemas de saúde, questões '
                                + 'envolvendo dependentes ou familiares, falta de energia ou internet, falhas em '
                                + 'equipamentos, entre outras situações semelhantes. A comunicação deve ocorrer '
                                + 'assim que o problema for identificado, permitindo a reorganização das demandas '
                                + 'e a adoção das medidas necessárias para minimizar impactos na operação e no '
                                + 'atendimento aos clientes.',
                            'Utilizar exclusivamente os canais oficiais da empresa (ex: WhatsApp, ChatPro, '
                                + 'E-Mail ou Chat Interno Próprio).',
                            'Estar disponível para reuniões de alinhamento e chamadas emergenciais.',
                            'Em reuniões importantes, a câmera deve estar ligada para garantir proximidade e '
                                + 'engajamento e, preferencialmente, utilizando notebook ou computador.',
                        ],
                    },
                ],
            },
            {
                numero: 6,
                titulo: 'Controle de Atividades',
                icone: 'mdi-clipboard-check',
                blocos: [
                    {
                        tipo: 'lista',
                        valor: [
                            'As tarefas são alinhadas diretamente entre gestor e colaborador por meio de canais '
                                + 'como WhatsApp ou reuniões via Google Meet. O desempenho é acompanhado de forma '
                                + 'contínua, com base em indicadores visíveis nos dashboards internos e feedbacks '
                                + 'periódicos, tanto em grupo quanto em reuniões individuais.',
                            'Não utilizamos ferramenta formal de controle de atividades para todas as áreas, '
                                + 'exceto em casos específicos como desenvolvimento.',
                            'Em caso de ausência, viagens ou qualquer situação que interfira no trabalho, o '
                                + 'colaborador deverá informar seu gestor com antecedência.',
                        ],
                    },
                ],
            },
            {
                numero: 7,
                titulo: 'Local de Trabalho e Ausências',
                icone: 'mdi-map-marker',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'O local base de trabalho é aquele previamente informado ao gestor. Caso haja '
                            + 'necessidade de trabalhar de outro lugar (como coworkings, cafés, casas de parentes '
                            + 'ou durante viagens), é obrigatório comunicar com antecedência.',
                    },
                    {
                        tipo: 'texto',
                        valor: 'Ausências durante o horário de trabalho — mesmo que breves — devem ser '
                            + 'informadas, salvo em situações de emergência.',
                    },
                ],
            },
            {
                numero: 8,
                titulo: 'Conduta e Penalidades',
                icone: 'mdi-gavel',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'O ZaperMenu se posiciona como uma equipe de alta performance. Por isso, '
                            + 'não toleramos:',
                    },
                    {
                        tipo: 'lista',
                        valor: [
                            'Falta de comprometimento ou atrasos recorrentes.',
                            'Uso inadequado dos equipamentos da empresa.',
                            'Falta de comunicação ou "sumiços" sem aviso prévio.',
                            'Queda de produtividade por negligência com o ambiente de trabalho.',
                        ],
                    },
                    {
                        tipo: 'texto',
                        valor: 'Evitar o uso de redes sociais durante o expediente, salvo quando relacionado '
                            + 'diretamente às atividades de trabalho.',
                    },
                    {
                        tipo: 'texto',
                        valor: 'É proibido manter notificações habilitadas — principalmente notificações '
                            + 'sonoras — que não tenham relação com o trabalho, a fim de evitar distrações e '
                            + 'garantir foco total nas entregas.',
                    },
                    {
                        tipo: 'texto',
                        valor: 'Tais condutas poderão resultar em advertências, desligamento ou término '
                            + 'de contrato.',
                    },
                ],
            },
            {
                numero: 9,
                titulo: 'Benefícios do Regime Home Office',
                icone: 'mdi-home-heart',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'Reconhecemos os benefícios do trabalho remoto: mais tempo com a família, '
                            + 'economia, conforto e autonomia. No entanto, esses benefícios são sustentáveis '
                            + 'somente quando todos assumem sua parte com profissionalismo e responsabilidade.',
                    },
                ],
            },
            {
                numero: 10,
                titulo: 'Aceite e Comprometimento',
                icone: 'mdi-handshake',
                blocos: [
                    {
                        tipo: 'texto',
                        valor: 'Todos os colaboradores, CLT ou freelancers, declaram estar cientes e de acordo '
                            + 'com estas normativas, comprometendo-se a cumpri-las integralmente ao darem início '
                            + 'ou continuidade ao trabalho remoto no ZaperMenu. Em caso de dúvidas, procure seu '
                            + 'gestor direto.',
                    },
                ],
            },
        ],
    }),

    methods: {
        irPara(numero) {
            // appOffset (padrão do goTo) já desconta a altura do v-app-bar;
            // o offset abaixo é só uma folga visual.
            this.$vuetify.goTo(`#secao-${numero}`, { offset: 12 });
        },
    },
};
</script>

<style scoped>
.doc-titulo {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
}

.doc-subtitulo {
    font-size: 14px;
    letter-spacing: 2px;
    text-transform: uppercase;
    opacity: 0.7;
}

.doc-indice {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}

.doc-corpo {
    max-width: 860px;
    margin: 0 auto;
}

.doc-secao:last-of-type .doc-separador {
    display: none;
}

.doc-secao-titulo {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
}

.doc-texto {
    font-size: 15px;
    line-height: 1.75;
    text-align: justify;
    margin-bottom: 16px;
}

.doc-lista {
    font-size: 15px;
    line-height: 1.75;
    margin-bottom: 16px;
    padding-left: 20px;
}

.doc-lista li {
    margin-bottom: 10px;
    text-align: justify;
}

.doc-destaque {
    font-size: 14px;
    line-height: 1.6;
    text-align: left;
}

.doc-rodape {
    font-size: 13px;
    font-style: italic;
    opacity: 0.7;
}

@media (max-width: 600px) {
    .doc-texto,
    .doc-lista,
    .doc-lista li {
        text-align: left;
    }
}
</style>
